import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import {
  navigationReducer,
  initialNavigationState,
  setOpenDropdown as setOpenDropdownAction,
  toggleMobileMenu as toggleMobileMenuAction,
  closeMobileMenu as closeMobileMenuAction,
  closeAll as closeAllAction,
} from './NavigationState';

interface NavigationContextValue {
  lang: string;
  isMobileMenuOpen: boolean;
  openDropdown: string | null;
  setOpenDropdown: (id: string | null) => void;
  toggleMobileMenu: () => void;
  closeMobileMenu: () => void;
  closeAll: () => void;
}

interface NavigationProviderProps {
  children: ReactNode;
}

interface RootState {
  intl: {
    locale: string;
  };
}

export const NavigationContext =
  createContext<NavigationContextValue | undefined>(undefined);

export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error(
      'useNavigation must be used within a NavigationProvider',
    );
  }

  return context;
};

export const NavigationProvider = ({ children }: NavigationProviderProps) => {
  const lang = useSelector(
    (state: RootState) => state.intl.locale,
    shallowEqual,
  );

  const [state, dispatch] = useReducer(
    navigationReducer,
    initialNavigationState,
  );

  const { isMobileMenuOpen, openDropdown } = state;

  const setOpenDropdown = useCallback((id: string | null) => {
    dispatch(setOpenDropdownAction(id));
  }, []);

  const toggleMobileMenu = useCallback(() => {
    dispatch(toggleMobileMenuAction());
  }, []);

  const closeMobileMenu = useCallback(() => {
    dispatch(closeMobileMenuAction());
  }, []);

  const closeAll = useCallback(() => {
    dispatch(closeAllAction());
  }, []);

  const value = useMemo<NavigationContextValue>(
    () => ({
      lang,
      isMobileMenuOpen,
      openDropdown,
      setOpenDropdown,
      toggleMobileMenu,
      closeMobileMenu,
      closeAll,
    }),
    [
      lang,
      isMobileMenuOpen,
      openDropdown,
      setOpenDropdown,
      toggleMobileMenu,
      closeMobileMenu,
      closeAll,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};